import type { FormEvent } from 'react';

import { use } from 'react';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import z from 'zod';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';

import { ModalInstanceContext } from '../../../contexts/ModalContext';

import { useAppForm } from '../../../form-hooks/form';

export interface ImportSpaceModalContentProps {
  /**
   * Import mode
   */
  mode: 'importSpace' | 'importCTD';
  /**
   * On submit callback, should return true if import was successful and modal can be closed
   */
  onSubmit: (value: { files: File[] }) => Promise<boolean>;
  /**
   * test id
   */
  testId?: string;
}

const contentTranslationKeys = {
  importSpace: 'SpaceSynchronization.Import.ImportSpace.Content',
  importCTD: 'SpaceSynchronization.Import.ImportCtd.Content',
};

const ACCEPTED_FILE_TYPES = ['application/zip'];

export const ImportSpaceModalContent = ({
  mode,
  onSubmit,
  testId = '',
}: ImportSpaceModalContentProps) => {
  const { t } = useTranslation();
  const modalInstance = use(ModalInstanceContext);

  const validationSchema = z.object({
    files: z
      .array(z.file())
      .min(1, t('SpaceSynchronization.Import.FileRequired')),
  });

  const form = useAppForm({
    defaultValues: {
      files: [] as File[],
    },
    onSubmit: async ({ value }) => {
      const ok = await onSubmit(value);
      if (ok) {
        modalInstance.resolve?.(true);
      }
    },
    validators: {
      onSubmit: validationSchema,
      onChange: validationSchema,
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const addFile = (files: File[]) => {
    const acceptedFiles = files.filter((file) =>
      ACCEPTED_FILE_TYPES.includes(file.type),
    );

    if (acceptedFiles.length !== files.length) {
      toast.error(t('SpaceSynchronization.Import.DnD.SupportedTypes'));
      return;
    }
    form.setFieldValue('files', files);
  };

  return (
    <div {...getTestProps(testId, 'import-modal-container')}>
      <form.AppForm>
        <form onSubmit={handleSubmit}>
          <p>
            <Trans
              i18nKey={contentTranslationKeys[mode]}
              components={{
                1: <strong />,
              }}
            />
          </p>
          <p className="mt-1 mb-6">
            {t('SpaceSynchronization.Import.Paragraph')}
          </p>
          <div className="text-left mb-8">
            <form.AppField name="files">
              {(field) => (
                <field.FilesSelectorField
                  testId={testId}
                  dropFileHandler={addFile}
                  dndLabel={t('SpaceSynchronization.Import.DnD.SupportedTypes')}
                  accept={ACCEPTED_FILE_TYPES.join(', ')}
                />
              )}
            </form.AppField>
          </div>
          <div
            className="absolute w-full left-0 bottom-0 p-6 flex justify-center items-center gap-2 bg-white
          border-t dark:bg-slate-900 dark:border-slate-700 rounded-b-md"
          >
            <Button
              buttonColor="blueBordered"
              onClick={() => modalInstance.resolve?.()}
              {...getTestProps(testId, 'cancel-button', 'testId')}
            >
              {t('Global.Cancel')}
            </Button>
            <form.Subscribe>
              {(state) => (
                <Button
                  type="submit"
                  disabled={!state.canSubmit || !state.isTouched}
                  {...getTestProps(testId, 'submit-button', 'testId')}
                >
                  {t('SpaceSynchronization.Import.ImportButton')}
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form>
      </form.AppForm>
    </div>
  );
};

export default ImportSpaceModalContent;
